import React from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate, useParams } from "react-router-dom";

import ContentCardList from "../../../Components/ContentCardList/ContentCardList";
import ContentCardsSkeleton from "../../../Components/ContentCardsSkeleton/ContentCardsSkeleton";
import PageHeader from "../../../Components/PageHeader/PageHeader";
import {
  getLibrarySection,
  LIBRARY_SECTIONS,
} from "../../../config/portalAluno/librarySections";
import LibraryPageSkeleton from "./LibraryPageSkeleton";
import useLibraryContents from "../../../hooks/PortalAluno/useLibraryContents";
import useLibraryListing from "../../../hooks/PortalAluno/useLibraryListing";
import "./styles.css";

const getErrorMessage = (error: unknown) =>
  error instanceof Error && error.message && error.message !== "Network Error"
    ? error.message
    : "Não foi possível carregar a Biblioteca. Verifique sua conexão e tente novamente.";

const LibraryPage = () => {
  const { libraryType } = useParams<{ libraryType: string }>();
  const selectedSection = getLibrarySection(libraryType);
  const navigate = useNavigate();
  const libraryQuery = useLibraryContents(!libraryType);
  const listingQuery = useLibraryListing(selectedSection);
  const listingCards = React.useMemo(() => {
    const seenCards = new Set<string>();

    return (listingQuery.data?.pages ?? []).flatMap((page) =>
      page.cards.filter((card) => {
        const key = `${card.sourceType ?? "conteudo"}-${card.id}`;

        if (seenCards.has(key)) return false;
        seenCards.add(key);
        return true;
      })
    );
  }, [listingQuery.data]);

  const sectionsWithContent = React.useMemo(
    () =>
      LIBRARY_SECTIONS.map((section) => ({
        cards: libraryQuery.data?.sections[section.key] ?? [],
        section,
      })).filter((entry) => entry.cards.length > 0),
    [libraryQuery.data]
  );

  if (libraryType && !selectedSection) {
    return (
      <section className="content-page library-page" aria-label="Biblioteca">
        <div className="library-page__inner">
          <button
            type="button"
            className="library-page__back"
            onClick={() => navigate("/app/biblioteca")}
          >
            <ArrowBackRoundedIcon aria-hidden="true" />
            <span>Voltar para a Biblioteca</span>
          </button>

          <div className="library-page__feedback" role="status">
            <h1>Seção não encontrada</h1>
            <p>Esta seção da Biblioteca não está disponível.</p>
          </div>
        </div>
      </section>
    );
  }

  if (selectedSection) {
    if (listingQuery.isLoading) return <LibraryPageSkeleton listing />;

    const hasAdditionalPageError =
      listingQuery.isError && Boolean(listingQuery.data);

    return (
      <section
        className="content-page library-page"
        aria-label={`Biblioteca - ${selectedSection.title}`}
      >
        <div className="library-page__inner">
          <button
            type="button"
            className="library-page__back"
            onClick={() => navigate("/app/biblioteca")}
          >
            <ArrowBackRoundedIcon aria-hidden="true" />
            <span>Voltar para a Biblioteca</span>
          </button>

          <PageHeader
            title={selectedSection.title}
            description={selectedSection.description}
          />

          {listingQuery.isError && !listingQuery.data ? (
            <div className="library-page__feedback" role="alert">
              <h1>Não foi possível abrir esta listagem</h1>
              <p>{getErrorMessage(listingQuery.error)}</p>
              <button type="button" onClick={() => listingQuery.refetch()}>
                Tentar novamente
              </button>
            </div>
          ) : (
            <>
              <ContentCardList
                title="Todos os conteúdos"
                cards={listingCards}
              />

              {listingQuery.hasNextPage && (
                <div className="library-page__pagination">
                  {listingQuery.isFetchingNextPage ? (
                    <div aria-label="Carregando mais conteúdos" aria-busy="true">
                      <ContentCardsSkeleton count={3} showTitle={false} />
                    </div>
                  ) : hasAdditionalPageError ? (
                    <div className="library-page__pagination-error" role="alert">
                      <p>Não foi possível carregar mais conteúdos.</p>
                      <button
                        type="button"
                        onClick={() => listingQuery.fetchNextPage()}
                      >
                        Tentar novamente
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => listingQuery.fetchNextPage()}
                    >
                      Carregar mais
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    );
  }

  if (libraryQuery.isLoading) return <LibraryPageSkeleton />;

  if (libraryQuery.isError && !libraryQuery.data) {
    return (
      <section className="content-page library-page" aria-label="Biblioteca">
        <div className="library-page__feedback" role="alert">
          <h1>Não foi possível abrir a Biblioteca</h1>
          <p>{getErrorMessage(libraryQuery.error)}</p>
          <button type="button" onClick={() => libraryQuery.refetch()}>
            Tentar novamente
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="content-page library-page" aria-label="Biblioteca">
      <div className="library-page__inner">
        <PageHeader
          title="Biblioteca"
          description="Materiais organizados por tipo de conteúdo para você acessar quando precisar."
        />

        {sectionsWithContent.length > 0 ? (
          <div className="library-page__sections">
            {sectionsWithContent.map(({ section, cards }) => {
              const openListing = () => navigate(section.route);

              return (
                <ContentCardList
                  key={section.key}
                  title={section.title}
                  cards={cards}
                  layout="row"
                  showViewAll
                  viewAllLabel={`Ver todos os conteúdos de ${section.title}`}
                  onViewAll={openListing}
                  seeMoreLabel={`Abrir a listagem completa de ${section.title}`}
                  onSeeMore={cards.length > 0 ? openListing : undefined}
                />
              );
            })}
          </div>
        ) : (
          <div className="library-page__feedback" role="status">
            <h1>Nenhum conteúdo disponível</h1>
            <p>
              Assim que houver material para o seu perfil, ele aparecerá aqui.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default LibraryPage;
